import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { RewardPool, RewardPoolsResponse } from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const REWARD_POOLS_QUERY_KEY = "wp:reward-pools";

export function useRewardPools(env: Environment, enabled = true) {
  const { data, ...rest } = useQuery<RewardPoolsResponse>({
    queryKey: [REWARD_POOLS_QUERY_KEY, env],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("gameReward", env);
      const res = await fetch(`${baseUrl}/v1/pools`, { cache: "no-store", signal });
      if (!res.ok) throw new Error(`Failed to fetch pools: ${res.status}`);
      const json: RewardPoolsResponse = await res.json();
      if (json.code !== 200) throw new Error(`Pools API error: ${json.code}`);
      return json;
    },
    enabled,
    staleTime: 0,
  });

  const crossPools = useMemo<RewardPool[]>(
    () => data?.data.pools.filter((p) => p.pool_type === "CrossPool") ?? [],
    [data],
  );

  const gamePools = useMemo<RewardPool[]>(
    () => data?.data.pools.filter((p) => p.pool_type === "GamePool") ?? [],
    [data],
  );

  return { crossPools, gamePools, allPools: data?.data.pools ?? [], ...rest };
}
